import {
  type PictureTypeKey,
  type PictureZenbujikoOptionKey,
} from "@/types/acquirebook";
import { z } from "zod";
import {
  PAST_DAYS_MIN,
  PAST_DAYS_MAX,
  PAST_DAYS_MIN_MESSAGE,
  PAST_DAYS_MAX_MESSAGE,
  PAST_DAYS_TYPE_MESSAGE,
} from "@/utils/pastDaysValidation";

/**
 * 登記/図面の取得方法用スキーマ
 */
const ReuseSchema = z.enum(["REUSE_NEW", "REUSE_OWN", "REUSE_TRUSTART"]);

/**
 * チェックボックス用のスキーマ
 */
const PictureZenbujikoOptionDetailSchema = z.object({
  checked: z.boolean(),
  disabled: z.boolean(), // TODO 本来はスキーマには不要
});

/**
 * 登記/図面 全部事項オプション用のスキーマ
 */
const PictureZenbujikoOptionSchema: z.ZodType<
  Record<
    PictureZenbujikoOptionKey,
    z.infer<typeof PictureZenbujikoOptionDetailSchema>
  >
> = z.object({
  SHINTAKUMOKUROKU: PictureZenbujikoOptionDetailSchema,
  KYODOTANPOMOKUROKU: PictureZenbujikoOptionDetailSchema,
  GENZAIJIKO: PictureZenbujikoOptionDetailSchema,
});

/**
 * 登記/図面種別用の詳細スキーマ
 */
const PictureTypeDetailSchema = z.object({
  checked: z.boolean(),
  disabled: z.boolean(), // TODO 本来はスキーマには不要
  reuse: ReuseSchema.nullable(),
});

/**
 * 登記/図面 全部事項オプション用の詳細スキーマ
 */
const PictureZenbujikoDetailSchema = z.object({
  checked: z.boolean(),
  disabled: z.boolean(),
  reuse: ReuseSchema.nullable(),
  option: PictureZenbujikoOptionSchema,
});

/**
 * 登記/図面種別選択チェックボックス用スキーマ
 */
const PictureTypeSchema: z.ZodType<{
  [key in PictureTypeKey]: key extends "ZENBUJIKO" // keyによって型が異なるので条件付き型を使用
    ? z.infer<typeof PictureZenbujikoDetailSchema>
    : z.infer<typeof PictureTypeDetailSchema>;
}> = z.object({
  SHOYUSHAJIKO: PictureTypeDetailSchema,
  ZENBUJIKO: PictureZenbujikoDetailSchema, // 全部事項は個別の詳細スキーマを使用
  CHISEKISOKURYOZU: PictureTypeDetailSchema,
  DENSHIKOZU: PictureTypeDetailSchema,
  CHIEKIKENZUMEN: PictureTypeDetailSchema,
  TATEMONOZUMEN: PictureTypeDetailSchema,
});

export const PastDaysNumberMessage = PAST_DAYS_TYPE_MESSAGE;

/**
 * 全部事項解析オプション用のスキーマ
 */
const AllMattersAnalyzeOptionsSchema = z.object({
  includeTitleSection: z.boolean(),
  includeKoku: z.boolean(),
  includeOtsuku: z.boolean(),
  analyzeMethodType: z.enum(["llm_api", "rule_based"]),
});

/**
 * 登記/図面種別選択チェックボックスのバリデーション関数
 * @param isDataFetched 外部データが取得済みかどうか
 * @returns 登記/図面種別選択チェックボックス用のバリデーションスキーマ
 */
const createPictureCheckOptionsValidator = (
  isDataFetched: boolean
): z.ZodType<z.infer<typeof PictureTypeSchema>> => {
  return PictureTypeSchema.superRefine((val, ctx) => {
    // 登記/図面種別のチェックボックスのバリデーション
    if (!Object.values(val).some((detail) => detail.checked)) {
      // 一つでもチェックがついていない場合はエラー
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "取得したい登記/図面を選択してください",
      });
      return;
    }

    // 全部事項オブションバリデーション
    if (
      val.ZENBUJIKO.option.GENZAIJIKO.checked &&
      !(
        val.ZENBUJIKO.option.SHINTAKUMOKUROKU.checked ||
        val.ZENBUJIKO.option.KYODOTANPOMOKUROKU.checked
      )
    ) {
      // 全部事項オプションのチェックボックスがついている、かつ、信託目録か共同担保目録が選択されていない場合はエラー
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "現在事項を取得する場合には信託目録か共同担保目録を選択してください",
      });
    }

    // 登記/図面種別のreuseのバリデーション
    // 初期値を入れているのでエラーにはならないはずだが、予期せぬバグで実行されることを防ぐために追加してます。
    // 外部データが取得済みの場合のみチェックする
    if (
      isDataFetched &&
      Object.values(val).some(
        (detail) => detail.checked && detail.reuse == null
      )
    ) {
      // 登記/図面種別のチェックボックスが付いている、かつ、reuseがnullの場合はエラー
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "選択された登記/図面に対して取得する内容を選択してください",
      });
    }
  });
};

/**
 * 登記1件取得UIのスキーマ
 */
const BaseAcquireBookFormSchema = z.object({
  realEstateTypeEng: z.string().min(1, "不動産種別を選択してください"),
  prefectureId: z.string().min(1, "都道府県を選択してください"),
  chibanKaokuNumbers: z
    .array(z.string())
    .min(1, "地番または家屋番号を入力してください"),
  // 登記/図面種別選択チェックボックス
  pictureCheckOptions: createPictureCheckOptionsValidator(false),
  identifyName: z.string().max(256, "識別名は256文字以内です"),
  pastDays: z
    .number({
      invalid_type_error: PastDaysNumberMessage,
      required_error: PastDaysNumberMessage,
    })
    .min(PAST_DAYS_MIN, PAST_DAYS_MIN_MESSAGE)
    .max(PAST_DAYS_MAX, PAST_DAYS_MAX_MESSAGE),
  requestClosureBook: z.boolean(),
});

/**
 * 全部事項解析オプションの共通バリデーション（最低1つ選択必須）
 */
const ALL_MATTERS_ANALYZE_OPTIONS_REFINE = {
  message: "解析オプションは最低1つ選択してください",
  path: ["allMattersAnalyzeOptions"] as Array<string | number>,
};

const refineAllMattersAnalyzeOptions = <
  T extends z.ZodType<
    {
      withAllMattersAnalyze?: boolean;
      allMattersAnalyzeOptions?: z.infer<typeof AllMattersAnalyzeOptionsSchema>;
    },
    z.ZodTypeDef,
    unknown
  >
>(
  schema: T
): z.ZodEffects<T> =>
  schema.refine((data) => {
    if (!data.withAllMattersAnalyze) return true;
    const opts = data.allMattersAnalyzeOptions;
    return (
      opts?.includeTitleSection === true ||
      opts?.includeKoku === true ||
      opts?.includeOtsuku === true
    );
  }, ALL_MATTERS_ANALYZE_OPTIONS_REFINE);

/**
 * 登記1件取得UIのスキーマ(ページ用) - Base
 */
const AcquireBookPageFormSchemaBase = BaseAcquireBookFormSchema.extend({
  cityCode: z.string().min(1, "市区町村を選択してください"),
  locationWithoutCity: z.string(),
  withOwnerAnalyze: z.boolean(),
  withAllMattersAnalyze: z.boolean(),
  allMattersAnalyzeOptions: AllMattersAnalyzeOptionsSchema,
});

/**
 * 登記1件取得UIのスキーマ(ページ用) - 動的pictureCheckOptions + 解析オプションrefine
 * @param isDataFetched 外部データが取得済みかどうか
 */
export const createAcquireBookPageFormSchema = (
  isDataFetched: boolean
): z.ZodEffects<
  ReturnType<
    typeof AcquireBookPageFormSchemaBase.extend<{
      pictureCheckOptions: ReturnType<
        typeof createPictureCheckOptionsValidator
      >;
    }>
  >
> =>
  refineAllMattersAnalyzeOptions(
    AcquireBookPageFormSchemaBase.extend({
      pictureCheckOptions: createPictureCheckOptionsValidator(isDataFetched),
    })
  );

/**
 * 登記1件取得UIのスキーマ(ページ用) - refine適用版（静的・後方互換用）
 */
const AcquireBookPageFormSchema = refineAllMattersAnalyzeOptions(
  AcquireBookPageFormSchemaBase
);

/**
 * 登記1件取得UIのスキーマ(モーダル用) - Base
 */
const AcquireBookModalFormSchemaBase = BaseAcquireBookFormSchema.extend({
  prefectureName: z.string(),
  location: z.string().min(1, "所在を入力してください"),
  withOwnerAnalyze: z.boolean(),
  withAllMattersAnalyze: z.boolean(),
  allMattersAnalyzeOptions: AllMattersAnalyzeOptionsSchema,
});

/**
 * 登記1件取得UIのスキーマ(モーダル用) - 動的pictureCheckOptions + 解析オプションrefine
 * @param isDataFetched 外部データが取得済みかどうか
 */
export const createAcquireBookModalFormSchema = (
  isDataFetched: boolean
): z.ZodEffects<
  ReturnType<
    typeof AcquireBookModalFormSchemaBase.extend<{
      pictureCheckOptions: ReturnType<
        typeof createPictureCheckOptionsValidator
      >;
    }>
  >
> =>
  refineAllMattersAnalyzeOptions(
    AcquireBookModalFormSchemaBase.extend({
      pictureCheckOptions: createPictureCheckOptionsValidator(isDataFetched),
    })
  );

/**
 * 登記1件取得UIのスキーマ(モーダル用) - refine適用版（静的・後方互換用）
 */
const AcquireBookModalFormSchema = refineAllMattersAnalyzeOptions(
  AcquireBookModalFormSchemaBase
);

export {
  ReuseSchema,
  PictureZenbujikoOptionDetailSchema,
  PictureZenbujikoOptionSchema,
  PictureZenbujikoDetailSchema,
  PictureTypeDetailSchema,
  PictureTypeSchema,
  BaseAcquireBookFormSchema,
  AcquireBookPageFormSchema,
  AcquireBookPageFormSchemaBase,
  AcquireBookModalFormSchema,
  AcquireBookModalFormSchemaBase,
  createPictureCheckOptionsValidator,
  AllMattersAnalyzeOptionsSchema,
};
